// Document library: files the team can send into a conversation.
// Route: /documents

import { useEffect, useState } from "react";

import { http, qs } from "../api";
import { useSession } from "../App";
import {
  Badge,
  Confirm,
  Dialog,
  DialogFooter,
  Empty,
  Field,
  Pager,
  RelativeTime,
  Search,
  Spinner,
  Table,
  useAsync,
  useToast,
  type Column,
} from "../ui";
import {
  PAGE_SIZE,
  errMessage,
  ErrorBanner,
  useDebounced,
  downloadBlob,
} from "./shared";

// ============================================================ documents

interface DocumentRow {
  id: string;
  name: string;
  original_name: string;
  mime_type: string;
  file_size: number;
  file_extension: string;
  uploaded_by_id: string;
  uploaded_by_name: string;
  created_at: string;
}

interface DocumentListPayload {
  documents?: DocumentRow[];
  total?: number;
}

const MAX_DOCUMENT_SIZE = 50 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`;
}

function fileTypeLabel(mime: string): string {
  if (mime.startsWith("image/")) return "Image";
  if (mime.startsWith("audio/")) return "Audio";
  if (mime.startsWith("video/")) return "Video";
  if (mime === "application/pdf") return "PDF";
  if (mime.includes("word")) return "Word";
  if (mime.includes("excel") || mime.includes("spreadsheet")) return "Excel";
  if (mime === "text/plain") return "Text";
  return "File";
}

function fileGlyph(mime: string): string {
  if (mime.startsWith("image/")) return "▣";
  if (mime.startsWith("audio/")) return "♪";
  if (mime.startsWith("video/")) return "▶";
  if (mime === "application/pdf" || mime.includes("word") || mime.includes("text")) return "▤";
  return "▥";
}

export function DocumentsView() {
  const { can } = useSession();
  const toast = useToast();
  const canWrite = can("documents", "write");
  const canDelete = can("documents", "delete");

  const [search, setSearch] = useState("");
  const query = useDebounced(search);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const { data, error, loading, reload } = useAsync<DocumentListPayload>(
    () =>
      http.get<DocumentListPayload>(
        `/documents${qs({ search: query, page, limit: PAGE_SIZE })}`
      ),
    [query, page]
  );
  const documents = data?.documents ?? [];
  const total = data?.total ?? documents.length;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState<DocumentRow | null>(null);

  const upload = async () => {
    if (!file) {
      toast.push("Choose a file to upload", "critical");
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE) {
      toast.push("That file is larger than 50 MB", "critical");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (name.trim()) form.append("name", name.trim());
      await http.upload("/documents", form);
      toast.push("Document uploaded", "good");
      setUploadOpen(false);
      setFile(null);
      setName("");
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not upload the document"), "critical");
    } finally {
      setUploading(false);
    }
  };

  const download = async (doc: DocumentRow) => {
    try {
      const res = await http.raw(`/documents/${doc.id}/download`);
      downloadBlob(await res.blob(), doc.original_name || doc.name);
    } catch (err) {
      toast.push(errMessage(err, "Could not download the document"), "critical");
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/documents/${toDelete.id}`);
      toast.push("Document deleted", "good");
      setToDelete(null);
      reload();
    } catch (err) {
      toast.push(errMessage(err, "Could not delete the document"), "critical");
      throw err;
    }
  };

  const columns: Column<DocumentRow>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (d) => (
        <span className="row">
          <span aria-hidden>{fileGlyph(d.mime_type)}</span>
          <span className="truncate" title={d.name} style={{ maxWidth: 280 }}>
            {d.name}
          </span>
        </span>
      ),
    },
    {
      key: "mime_type",
      header: "Type",
      render: (d) => <Badge tone="neutral">{fileTypeLabel(d.mime_type)}</Badge>,
    },
    {
      key: "file_size",
      header: "Size",
      sortable: true,
      align: "right",
      render: (d) => <span className="num">{formatFileSize(d.file_size)}</span>,
    },
    {
      key: "uploaded_by_name",
      header: "Uploaded by",
      render: (d) => d.uploaded_by_name || <span className="muted">—</span>,
    },
    {
      key: "created_at",
      header: "Uploaded",
      sortable: true,
      render: (d) => <RelativeTime value={d.created_at} />,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (d) => (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn icon sm ghost" title="Download" onClick={() => void download(d)}>
            ⭳
          </button>
          <button
            className="btn icon sm ghost"
            title="Delete"
            disabled={!canDelete}
            onClick={() => setToDelete(d)}
          >
            ✕
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Documents</h1>
          <div className="sub">Files the team can send into a conversation.</div>
        </div>
        <div className="page-actions">
          <button className="btn primary" onClick={() => setUploadOpen(true)} disabled={!canWrite}>
            ⭱ Upload
          </button>
        </div>
      </div>

      <ErrorBanner error={error} what="Could not load the documents" />

      <div className="card flush">
        <div className="card-head">
          <h3>Document library</h3>
          <Search value={search} onChange={setSearch} placeholder="Search documents…" />
        </div>
        <div className="card-body">
          <Table
            columns={columns}
            rows={documents}
            loading={loading}
            empty={
              <Empty
                title={query ? "No matching documents" : "No documents yet"}
                body={
                  query
                    ? `Nothing matches "${query}".`
                    : "Upload a policy document or a form so agents can send it without leaving the inbox."
                }
                icon="▥"
                action={
                  canWrite ? (
                    <button className="btn primary sm" onClick={() => setUploadOpen(true)}>
                      ⭱ Upload
                    </button>
                  ) : undefined
                }
              />
            }
          />
        </div>
        <div className="card-foot">
          <Pager
            total={total}
            limit={PAGE_SIZE}
            offset={(page - 1) * PAGE_SIZE}
            itemName="documents"
            onChange={(offset) => setPage(Math.floor(offset / PAGE_SIZE) + 1)}
          />
        </div>
      </div>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload a document">
        <Field label="File" hint="50 MB at most." required>
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
        <Field label="Name" hint="Leave blank to use the file name.">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <DialogFooter>
          <button className="btn" onClick={() => setUploadOpen(false)} disabled={uploading}>
            Cancel
          </button>
          <button className="btn primary" onClick={() => void upload()} disabled={!file || uploading}>
            {uploading && <Spinner label="Uploading" />}
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </DialogFooter>
      </Dialog>

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this document?"
        body={`"${toDelete?.name ?? ""}" will be removed from the library. Messages that already carry it are unaffected.`}
        danger
      />
    </div>
  );
}
